import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ApiConstants } from "../../../main/constants/ApiConstants";
import { useTranslation } from "react-i18next";
import { EntityFileMetadata, getMetadataFileUrl } from "../../../main/utils/entityGallery";
import useGetUser from "../../../main/hooks/useGetUser";
import "../../../main/theme/theme.css";
import { AppTheme, getStoredMainLandingTheme, MAIN_LANDING_THEMES } from "../../../main/theme/publicTheme";
import PublicLanguageSwitch from "./PublicLanguageSwitch";
import { formatPublicPrice } from "./publicBookingCurrency";
import "./PublicExperience.css";

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

interface Props {
  hotelId: number | null;
  room: any;
  roomPhotos?: EntityFileMetadata[];
  selectedCurrency?: string;
  checkInDate: string;
  checkOutDate: string;
  onClose: () => void;
  onSuccess?: (request: {
    id?: number;
    guestName: string;
    guestEmail: string;
    roomTypeName: string;
    roomNumber?: string;
    checkInDate: string;
    checkOutDate: string;
    created?: string;
    status?: number;
  }) => void;
}

type RateOption = {
  roomRateId: number;
  ratePlanId: number;
  ratePlanName: string;
  pricePerNight: number;
  totalPrice: number;
};

const THEME_LABELS: Record<AppTheme, string> = {
  light: "Light",
  sky: "Sky",
  dark: "Dark",
  ocean: "Ocean",
  pearl: "Pearl",
  forest: "Forest",
};

const PublicBook = ({ hotelId, room, roomPhotos = [], selectedCurrency = "ALL", checkInDate, checkOutDate, onClose, onSuccess }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useGetUser() as any;
  const [theme, setTheme] = useState<AppTheme>(getStoredMainLandingTheme);
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const themeDropdownRef = useRef<HTMLDivElement | null>(null);
  const [selectedPhotoId, setSelectedPhotoId] = useState<number | null>(roomPhotos[0]?.id ?? null);
  const [loading, setLoading] = useState(false);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [specialRequests, setSpecialRequests] = useState("");
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();

  useEffect(() => {
    if (!isThemeOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!themeDropdownRef.current?.contains(event.target as Node)) {
        setIsThemeOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsThemeOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isThemeOpen]);

  const rateOptions = useMemo<RateOption[]>(() => {
    const options = Array.isArray(room?.rateOptions) ? room.rateOptions : [];

    return options
      .map((option: any): RateOption => ({
        roomRateId: Number(option?.roomRateId ?? option?.RoomRateId ?? 0),
        ratePlanId: Number(option?.ratePlanId ?? option?.RatePlanId ?? 0),
        ratePlanName: option?.ratePlanName ?? option?.RatePlanName ?? "",
        pricePerNight: Number(option?.pricePerNight ?? option?.PricePerNight ?? 0),
        totalPrice: Number(option?.totalPrice ?? option?.TotalPrice ?? 0),
      }))
      .filter((option: RateOption) => option.ratePlanId > 0);
  }, [room]);

  const defaultRatePlanId = useMemo(() => {
    const recommendedRatePlanId = Number(room?.recommendedRatePlanId ?? room?.RecommendedRatePlanId ?? 0);

    if (recommendedRatePlanId > 0 && rateOptions.some((option) => option.ratePlanId === recommendedRatePlanId)) {
      return String(recommendedRatePlanId);
    }

    return rateOptions[0]?.ratePlanId ? String(rateOptions[0].ratePlanId) : "";
  }, [rateOptions, room]);

  const [selectedRatePlanId, setSelectedRatePlanId] = useState(defaultRatePlanId);
  const selectedRateOption = useMemo(
    () => rateOptions.find((option) => String(option.ratePlanId) === selectedRatePlanId) ?? null,
    [rateOptions, selectedRatePlanId]
  );

  useEffect(() => {
    setSelectedRatePlanId(defaultRatePlanId);
  }, [defaultRatePlanId]);

  useEffect(() => {
    setSelectedPhotoId(roomPhotos[0]?.id ?? null);
  }, [roomPhotos]);

  const guestName = useMemo(() => {
    if (!currentUser) return "";

    const combined = `${currentUser.firstName ?? ""} ${currentUser.lastName ?? ""}`.trim();
    if (combined) return combined;
    if (currentUser.userName?.trim()) return currentUser.userName.trim();
    return currentUser.email ?? "";
  }, [currentUser]);

  const handleSubmitRequest = async () => {
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);

    if (!currentUser) {
      navigate("/login");
      return;
    }

    if (!hotelId) {
      toast.error(t("publicBooking.hotelInfoMissing"));
      return;
    }

    if (!room?.roomTypeId) {
      toast.error(t("publicBooking.roomTypeRequired"));
      return;
    }

    if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime()) || checkIn >= checkOut) {
      toast.error(t("publicBooking.invalidStay"));
      return;
    }

    if (!adults || adults <= 0) {
      toast.error(t("publicBooking.minAdults"));
      return;
    }

    if (rateOptions.length === 0 || !selectedRatePlanId) {
      toast.error(t("reservationDesk.noRateOptions"));
      return;
    }

    const guestFirstName = currentUser.firstName?.trim() || guestName;
    const guestLastName = currentUser.lastName?.trim() || guestName;
    const guestPhone = currentUser.phone?.trim() || "";
    const guestEmail = (currentUser.email ?? "").trim().toLowerCase();

    try {
      setLoading(true);

      const response = await publicApi.post(ApiConstants.publicHotel.bookingRequest, {
        hotelId,
        guestFirstName,
        guestLastName,
        guestEmail,
        guestPhone,
        requestedRoomTypeId: room?.roomTypeId,
        requestedRoomId: room?.roomId ? Number(room.roomId) : null,
        requestedRatePlanId: selectedRatePlanId ? Number(selectedRatePlanId) : null,
        checkInDate,
        checkOutDate,
        adults,
        children,
        specialRequests: specialRequests.trim(),
      });

      const requestId = Number(response?.data?.id ?? response?.data?.Id ?? 0);
      toast.success(t("publicBooking.requestSentToast"));
      onSuccess?.({
        id: requestId || undefined,
        guestName,
        guestEmail,
        roomTypeName: room?.roomTypeName ?? t("reservationDesk.room"),
        roomNumber: room?.roomNumber ?? "",
        checkInDate,
        checkOutDate,
        created: response?.data?.created ?? response?.data?.Created ?? new Date().toISOString(),
        status: response?.data?.status ?? response?.data?.Status ?? 1,
      });
      onClose();
    } catch (err: any) {
      const validationErrors = err?.response?.data?.errors;
      const responseMessage =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.response?.data?.title ||
        (typeof err?.response?.data === "string" ? err.response.data : "");

      const firstValidationMessage =
        validationErrors &&
        Object.values(validationErrors)
          .flat()
          .find((message) => typeof message === "string");

      toast.error(firstValidationMessage || responseMessage || err?.message || t("publicBooking.requestFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`public-book-card bcShell bcTheme-${theme}`}>
      {roomPhotos.length ? (
        <div className="public-book-gallery">
          <img
            src={getMetadataFileUrl(selectedPhotoId ?? roomPhotos[0]?.id)}
            alt={room?.roomNumber ? `Room ${room.roomNumber}` : "Room"}
            className="public-book-gallery-cover"
          />
          {roomPhotos.length > 1 ? (
            <div className="public-book-gallery-strip">
              {roomPhotos.slice(0, 5).map((photo) => (
                <button
                  key={photo.id}
                  type="button"
                  className={`public-book-gallery-thumb-btn ${selectedPhotoId === photo.id ? "is-active" : ""}`}
                  onClick={() => setSelectedPhotoId(photo.id)}
                >
                  <img
                    src={getMetadataFileUrl(photo.id)}
                    alt={room?.roomNumber ? `Room ${room.roomNumber}` : "Room"}
                    className="public-book-gallery-thumb"
                  />
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="public-book-header">
        <div className="public-book-title-wrap">
          <div className="public-book-topline">
            <span className="public-book-pill">
              <HotelOutlinedIcon fontSize="inherit" />
              {t("reservationDesk.sendBookingRequest", { defaultValue: "Booking request" })}
            </span>

            <div className="public-book-topline-actions">
              <div className="theme-dropdown public-book-theme-dropdown" ref={themeDropdownRef}>
                <button
                  type="button"
                  className="theme-dropdown-trigger public-book-theme-trigger"
                  onClick={() => setIsThemeOpen((current) => !current)}
                  aria-haspopup="menu"
                  aria-expanded={isThemeOpen}
                  aria-label={t("generic.theme", { defaultValue: "Theme" })}
                >
                  <PaletteOutlinedIcon fontSize="inherit" />
                  <span>{THEME_LABELS[theme]}</span>
                  <KeyboardArrowDownRoundedIcon fontSize="inherit" />
                </button>

                {isThemeOpen ? (
                  <div className="theme-dropdown-menu public-book-theme-menu" role="menu">
                    {MAIN_LANDING_THEMES.map((themeOption) => (
                      <button
                        key={themeOption}
                        type="button"
                        className={`theme-dropdown-item ${theme === themeOption ? "active" : ""}`}
                        onClick={() => {
                          setTheme(themeOption);
                          setIsThemeOpen(false);
                        }}
                        role="menuitemradio"
                        aria-checked={theme === themeOption}
                      >
                        <span className={`theme-dot theme-dot--${themeOption}`} />
                        <span>{THEME_LABELS[themeOption]}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <PublicLanguageSwitch />

              <button type="button" className="public-book-close-btn" onClick={onClose} aria-label={t("generic.close", { defaultValue: "Close" })}>
                <CloseRoundedIcon fontSize="inherit" />
              </button>
            </div>
          </div>

          <h2 className="public-book-title">
            {t("reservationDesk.roomNumber", { roomNumber: room?.roomNumber || "-" })}
          </h2>
          <p className="public-book-note">
            {t("publicBooking.requestFlowHint", {
              defaultValue: "Review the stay details below. Logged-in users can send the request immediately."
            })}
          </p>
        </div>
      </div>

      <section className="public-book-section">
        <h3 className="public-book-section-title">{t("publicBooking.stayDetails", { defaultValue: "Stay details" })}</h3>
        <div className="public-book-summary-grid">
          <div className="public-book-detail">
            <label>{t("reservationDesk.roomType")}</label>
            <strong>{room?.roomTypeName || "-"}</strong>
          </div>
          <div className="public-book-detail">
            <label>{t("reservationDesk.ratePlan")}</label>
            <select
              value={selectedRatePlanId}
              onChange={(e) => setSelectedRatePlanId(e.target.value)}
              disabled={rateOptions.length === 0}
            >
              {rateOptions.length === 0 ? <option value="">{t("reservationDesk.noRateOptions")}</option> : null}
              {rateOptions.map((option) => (
                <option key={option.ratePlanId} value={option.ratePlanId}>
                  {option.ratePlanName}
                </option>
              ))}
            </select>
          </div>
          <div className="public-book-detail">
            <label>{t("reservationDesk.checkIn")}</label>
            <span>{checkInDate || "-"}</span>
          </div>
          <div className="public-book-detail">
            <label>{t("reservationDesk.checkOut")}</label>
            <span>{checkOutDate || "-"}</span>
          </div>
          <div className="public-book-detail">
            <label>{t("reservationDesk.pricePerNight")}</label>
            <strong>{selectedRateOption ? formatPublicPrice(selectedRateOption.pricePerNight, selectedCurrency, currentLanguage) : "-"}</strong>
          </div>
          <div className="public-book-detail">
            <label>{t("generic.total")}</label>
            <strong>{selectedRateOption ? formatPublicPrice(selectedRateOption.totalPrice, selectedCurrency, currentLanguage) : "-"}</strong>
          </div>
        </div>
      </section>

      <section className="public-book-section">
        <h3 className="public-book-section-title">{t("publicBooking.requestOptions", { defaultValue: "Request options" })}</h3>
        <div className="public-book-summary-grid">
          <div className="public-book-detail">
            <label htmlFor="public-book-adults">{t("publicBooking.adults")}</label>
            <input
              id="public-book-adults"
              type="number"
              min={1}
              value={adults}
              onChange={(e) => setAdults(Math.max(1, Number(e.target.value || 1)))}
            />
          </div>
          <div className="public-book-detail">
            <label htmlFor="public-book-children">{t("publicBooking.children")}</label>
            <input
              id="public-book-children"
              type="number"
              min={0}
              value={children}
              onChange={(e) => setChildren(Math.max(0, Number(e.target.value || 0)))}
            />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label htmlFor="public-book-special" className="public-book-section-title">
            {t("publicBooking.specialRequest")}
          </label>
          <textarea
            id="public-book-special"
            className="public-book-textarea"
            placeholder={t("publicBooking.specialRequestPlaceholder")}
            value={specialRequests}
            onChange={(e) => setSpecialRequests(e.target.value)}
          />
        </div>
      </section>

      {currentUser ? (
        <section className="public-book-section public-book-user-card">
          <h3 className="public-book-section-title">{t("publicBooking.requestOwner", { defaultValue: "Request owner" })}</h3>
          <div className="public-book-user-grid">
            <div className="public-book-detail">
              <label>{t("generic.user", { defaultValue: "User" })}</label>
              <strong>{guestName}</strong>
            </div>
            <div className="public-book-detail">
              <label>{t("generic.email")}</label>
              <span>{currentUser.email}</span>
            </div>
          </div>
          <p className="public-book-user-line">
            <strong>{t("publicBooking.loggedInHint", { defaultValue: "Logged in" })}:</strong>{" "}
            {t("publicBooking.loggedInRequestHint", {
              defaultValue: "We will use your saved public booking account details for this request."
            })}
          </p>
        </section>
      ) : (
        <section className="public-book-section public-book-login-box">
          <h3 className="public-book-section-title">{t("publicBooking.loginRequiredTitle", { defaultValue: "Login required" })}</h3>
          <p className="public-book-login-copy">
            {t("publicBooking.loginRequiredCopy", {
              defaultValue: "Please log in before sending the booking request. No guest detail form is needed here anymore."
            })}
          </p>
        </section>
      )}

      <div className="public-book-actions">
        <button type="button" onClick={onClose} className="public-theme-btn public-theme-btn--ghost">
          {t("generic.cancel")}
        </button>
        {currentUser ? (
          <button type="button" disabled={loading} className="public-book-send-btn" onClick={handleSubmitRequest}>
            <SendRoundedIcon fontSize="inherit" />
            {loading ? t("publicBooking.submitting") : t("publicBooking.submitRequest")}
          </button>
        ) : (
          <button type="button" className="public-book-login-btn" onClick={() => navigate("/login")}>
            <LoginOutlinedIcon fontSize="inherit" />
            {t("mainLanding.auth.login", { defaultValue: "Log in" })}
          </button>
        )}
      </div>
    </div>
  );
};

export default PublicBook;
